import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'
import { stimulus } from '~/init'

export default class MasterAppointmentProductsController extends Controller {
  static targets = ['searchInput', 'dropdown', 'productRows', 'emptyMessage']
  static values = { searchUrl: String, saveUrl: String }

  initialize () {
    this.products = new Map()
    this.searchTimeout = null
    this.searchAbort = null
  }

  connect () {
    this.products = new Map()
    this.productRowsTarget.querySelectorAll('[data-product-id]').forEach(row => {
      this.products.set(row.dataset.productId, true)
    })
    this.#toggleEmpty()
  }

  disconnect () {
    if (this.searchAbort) this.searchAbort.abort()
    clearTimeout(this.searchTimeout)
  }

  onSearch () {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.#hideDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => {
      if (this.searchAbort) this.searchAbort.abort()
      this.searchAbort = new AbortController()

      fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.searchAbort.signal
      })
        .then(r => r.json())
        .then(data => this.#renderDropdown(data))
        .catch(e => { if (e.name !== 'AbortError') this.#hideDropdown() })
    }, 300)
  }

  selectProduct (event) {
    const item = event.currentTarget
    const product = JSON.parse(item.dataset.product)

    if (this.products.has(String(product.id))) return

    this.#addProductRow(product)
    this.searchInputTarget.value = ''
    this.#hideDropdown()
  }

  removeProduct (event) {
    const row = event.currentTarget.closest('[data-product-id]')
    this.products.delete(row.dataset.productId)
    row.remove()
    this.#toggleEmpty()
  }

  save () {
    const products = []
    this.productRowsTarget.querySelectorAll('[data-product-id]').forEach(row => {
      const qty = parseFloat(row.querySelector('input[type="number"]')?.value) || 1
      products.push({ product_id: row.dataset.productId, quantity: qty })
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.saveUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        Accept: 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify({ products })
    })
      .then(r => {
        if (r.ok) return r.text()
        throw new Error('Save failed')
      })
      .then(html => {
        if (html) Turbo.renderStreamMessage(html)
      })
      .catch(() => {})
  }

  // Private

  #renderDropdown (data) {
    if (!data.length) {
      this.#hideDropdown()
      return
    }

    const html = data.map(p => {
      const added = this.products.has(String(p.id))
      return `
        <button type="button"
          class="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b border-border/30 last:border-b-0 flex items-center justify-between gap-4 ${added ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}"
          data-action="click->master-appointment-products#selectProduct"
          data-product='${JSON.stringify(p).replace(/'/g, '&#39;')}'>
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium truncate">${this.#escapeHtml(p.name)}</span>
            <span class="text-xs text-muted-foreground">${this.#escapeHtml(p.article)}${p.brand ? ' · ' + this.#escapeHtml(p.brand) : ''}</span>
          </div>
          <div class="flex items-center gap-2 shrink-0 text-xs">
            <span class="${p.stock > 0 ? 'text-green-600' : 'text-red-500'} tabular-nums">${p.stock} ${this.#escapeHtml(p.unit)}</span>
          </div>
        </button>
      `
    }).join('')

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove('hidden')
  }

  #hideDropdown () {
    this.dropdownTarget.classList.add('hidden')
    this.dropdownTarget.innerHTML = ''
  }

  #addProductRow (product, quantity = 1) {
    const id = String(product.id)
    this.products.set(id, true)

    const row = document.createElement('tr')
    row.dataset.productId = id
    row.className = 'border-b border-border/40 last:border-b-0'

    row.innerHTML = `
      <td class="py-2 pr-3">
        <div class="flex flex-col">
          <span class="text-sm font-medium truncate max-w-[200px]" title="${this.#escapeHtml(product.name)}">${this.#escapeHtml(product.name)}</span>
          <span class="text-xs text-muted-foreground">${this.#escapeHtml(product.article)}</span>
        </div>
      </td>
      <td class="py-2 px-2 w-24">
        <input type="number" value="${quantity}" min="0.1" step="0.1"
               class="form__field text-center text-sm tabular-nums w-full"
>
      </td>
      <td class="py-2 pl-2">
        <button type="button" class="text-muted-foreground hover:text-red-500 transition-colors"
                data-action="click->master-appointment-products#removeProduct">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </td>
    `

    this.productRowsTarget.querySelector('tbody').appendChild(row)
    this.#toggleEmpty()
  }

  #toggleEmpty () {
    const hasProducts = this.products.size > 0
    this.emptyMessageTarget.classList.toggle('hidden', hasProducts)
    this.productRowsTarget.classList.toggle('hidden', !hasProducts)
  }

  #escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}

stimulus.register('master-appointment-products', MasterAppointmentProductsController)
